import classNames from 'classnames'
import { NavLink, useLocation } from 'react-router-dom'
import s from './index.module.css'
import { CloseOutlined, MenuOutlined } from '@ant-design/icons'

import { useDispatch, useSelector } from 'react-redux'
import { selectProfile } from '@store/profile.reducer'
import { menuReportApi } from '@/api'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { actionMinimizedMenu } from '@store/menu.reducer'
import { useScreenWidth } from '@/hooks/useScreenWidth'
import {
  CLOSE_REPORTS_NAVIGATION_EVENT,
  OPEN_REPORTS_NAVIGATION_EVENT,
  setReportsNavigationHovered,
} from '@/helpers/reportsNavigation'
import {
  OTHER_REPORTS_FAMILY_ID,
  OTHER_REPORTS_FAMILY_TITLE,
  reportFamilies,
  resolveReportFamilyId,
} from './reportFamilies'

interface Link {
  title: string
  url: string
  family_id?: string | number | null
  family?: string
}

interface VisibleReportFamily {
  id: string
  title: string
  reports: Link[]
}

const getVisibleReportFamilies = (links: Link[]): VisibleReportFamily[] => {
  const reportsByFamily = new Map<string, Link[]>()

  links.forEach((link) => {
    const familyId = resolveReportFamilyId(
      link.url,
      link.family_id ?? link.family,
    )
    const familyReports = reportsByFamily.get(familyId) ?? []

    reportsByFamily.set(familyId, [...familyReports, link])
  })

  const families = reportFamilies
    .map(({ id, title }) => ({
      id,
      title,
      reports: reportsByFamily.get(id) ?? [],
    }))
    .filter((family) => family.reports.length)

  const otherReports = reportsByFamily.get(OTHER_REPORTS_FAMILY_ID) ?? []

  if (otherReports.length) {
    families.push({
      id: OTHER_REPORTS_FAMILY_ID,
      title: OTHER_REPORTS_FAMILY_TITLE,
      reports: otherReports,
    })
  }

  return families
}

const Reports = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const isMobile = useScreenWidth(992)
  const userRole = useSelector(selectProfile)?.role
  const [linksList, setLinksList] = useState<Link[]>([])
  const [linksLoaded, setLinksLoaded] = useState(false)
  const [activeFamilyId, setActiveFamilyId] = useState<string | null>(null)
  const [isNavigationOpen, setIsNavigationOpen] = useState(
    location.pathname === '/reports',
  )
  const closeNavigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )
  const isReportsPath = location.pathname.toLowerCase().startsWith('/reports')

  const visibleFamilies = useMemo(
    () => getVisibleReportFamilies(linksList),
    [linksList],
  )
  const activeFamily = visibleFamilies.find(
    (family) => family.id === activeFamilyId,
  )
  const hasReports = linksList.length > 0
  const shouldShowNavigation = isNavigationOpen
  const shouldShowNavigationToggle =
    isReportsPath && hasReports && !isNavigationOpen

  const cancelNavigationClose = useCallback(() => {
    if (closeNavigationTimeoutRef.current === null) return

    clearTimeout(closeNavigationTimeoutRef.current)
    closeNavigationTimeoutRef.current = null
  }, [])

  const closeNavigation = useCallback(() => {
    cancelNavigationClose()
    setReportsNavigationHovered(false)
    setActiveFamilyId(null)
    setIsNavigationOpen(false)
  }, [cancelNavigationClose])

  const scheduleNavigationClose = useCallback(() => {
    cancelNavigationClose()
    closeNavigationTimeoutRef.current = setTimeout(closeNavigation, 180)
  }, [cancelNavigationClose, closeNavigation])

  useEffect(() => {
    if (isReportsPath || isNavigationOpen) {
      dispatch(actionMinimizedMenu())
    }
  }, [dispatch, isNavigationOpen, isReportsPath])

  useEffect(() => {
    if (!userRole) return undefined

    let isMounted = true

    setLinksLoaded(false)
    menuReportApi
      .getMenu(userRole)
      .then((response) => {
        if (isMounted) setLinksList(response.data ?? [])
      })
      .catch(() => {
        if (isMounted) setLinksList([])
      })
      .finally(() => {
        if (isMounted) setLinksLoaded(true)
      })

    return () => {
      isMounted = false
    }
  }, [userRole])

  useEffect(() => {
    const openNavigation = () => {
      cancelNavigationClose()
      setActiveFamilyId(null)
      setIsNavigationOpen(true)
    }

    window.addEventListener(OPEN_REPORTS_NAVIGATION_EVENT, openNavigation)
    window.addEventListener(
      CLOSE_REPORTS_NAVIGATION_EVENT,
      scheduleNavigationClose,
    )

    return () => {
      window.removeEventListener(OPEN_REPORTS_NAVIGATION_EVENT, openNavigation)
      window.removeEventListener(
        CLOSE_REPORTS_NAVIGATION_EVENT,
        scheduleNavigationClose,
      )
      cancelNavigationClose()
    }
  }, [cancelNavigationClose, scheduleNavigationClose])

  useEffect(() => {
    if (location.pathname === '/reports') {
      setIsNavigationOpen(true)
      return
    }

    if (!location.pathname.toLowerCase().startsWith('/reports')) {
      setIsNavigationOpen(false)
    }
  }, [location.pathname])

  useEffect(() => {
    if (!visibleFamilies.length) return

    const familyByPath = visibleFamilies.find((family) =>
      family.reports.some((report) => report.url === location.pathname),
    )

    if (familyByPath) {
      setActiveFamilyId(familyByPath.id)
      return
    }

    if (
      location.pathname === '/reports' ||
      !location.pathname.toLowerCase().startsWith('/reports')
    ) {
      setActiveFamilyId(null)
    }
  }, [visibleFamilies, location.pathname])

  useEffect(() => {
    const appWrapper = document.querySelector('.app-wrapper')

    appWrapper?.classList.toggle(
      'app-wrapper-reports-navigation-visible',
      shouldShowNavigation && !isMobile,
    )
    appWrapper?.classList.toggle(
      'app-wrapper-reports-expanded',
      shouldShowNavigation && Boolean(activeFamily) && !isMobile,
    )
    appWrapper?.classList.toggle(
      'app-wrapper-reports-navigation-toggle-visible',
      shouldShowNavigationToggle && !isMobile,
    )

    return () => {
      appWrapper?.classList.remove('app-wrapper-reports-navigation-visible')
      appWrapper?.classList.remove('app-wrapper-reports-expanded')
      appWrapper?.classList.remove(
        'app-wrapper-reports-navigation-toggle-visible',
      )
    }
  }, [activeFamily, isMobile, shouldShowNavigation, shouldShowNavigationToggle])

  if (shouldShowNavigationToggle) {
    return (
      <button
        type="button"
        className={s.openNavigationButton}
        data-reports-navigation-toggle
        onClick={() => setIsNavigationOpen(true)}
        title="Открыть меню отчётов"
        aria-label="Открыть меню отчётов"
      >
        <MenuOutlined
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      </button>
    )
  }

  return (
    shouldShowNavigation && (
      <aside
        className={classNames(
          s.navigation,
          activeFamily && s.navigationExpanded,
        )}
        aria-label="Навигация по отчётам"
        onMouseEnter={() => {
          if (isMobile) return
          cancelNavigationClose()
          setReportsNavigationHovered(true)
        }}
        onMouseLeave={() => {
          if (isMobile) return
          setReportsNavigationHovered(false)
          scheduleNavigationClose()
        }}
      >
        <section className={s.familyPanel}>
          <div className={s.panelHeader}>
            <h2 className={s.panelTitle}>Все отчёты</h2>
            <button
              type="button"
              className={s.closeNavigationButton}
              onClick={closeNavigation}
              title="Скрыть меню отчётов"
              aria-label="Скрыть меню отчётов"
            >
              <CloseOutlined
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </button>
          </div>
          <nav className={s.navigationList} aria-label="Семейства отчётов">
            {!linksLoaded && (
              <span className={s.emptyNavigation}>Загрузка отчётов...</span>
            )}
            {linksLoaded && visibleFamilies.length === 0 && (
              <span className={s.emptyNavigation}>Нет доступных отчётов</span>
            )}
            {visibleFamilies.map((family) => (
              <button
                key={family.id}
                type="button"
                className={classNames(
                  s.familyButton,
                  activeFamilyId === family.id && s.familyButtonActive,
                )}
                onMouseEnter={() => {
                  if (!isMobile) setActiveFamilyId(family.id)
                }}
                onClick={() => setActiveFamilyId(family.id)}
              >
                <span>{family.title}</span>
                <span className={s.familyArrow} aria-hidden="true" />
              </button>
            ))}
          </nav>
        </section>

        {activeFamily && (
          <section className={s.reportsPanel}>
            <h2 className={s.panelTitle}>{activeFamily.title}</h2>
            <nav className={s.navigationList} aria-label={activeFamily.title}>
              {activeFamily.reports.map((report) => (
                <LinkItem
                  key={report.url}
                  label={report.title}
                  url={report.url}
                  onSelect={closeNavigation}
                />
              ))}
            </nav>
          </section>
        )}
      </aside>
    )
  )
}

const LinkItem = ({
  label,
  url,
  onSelect,
}: {
  label: string
  url: string
  onSelect: () => void
}): JSX.Element => {
  return (
    <NavLink
      exact
      to={url}
      className={s.reportLink}
      activeClassName={s.reportLinkActive}
      title={label}
      onClick={onSelect}
    >
      {label}
    </NavLink>
  )
}

export default Reports
